import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
    DepartmentVariableSettings,
    VariableTab,
} from '../../services/api/departmentVariablesService';
import { DepartmentUser, categoryTitle } from './utils/settingsView';
import CategoryCard from './CategoryCard';
import AddCategoryForm from './AddCategoryForm';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

interface SettingsOverviewProps {
    settings: DepartmentVariableSettings;
    users: DepartmentUser[];
    onSaved: () => Promise<void>;
}

const TABS: { value: VariableTab; labelKey: string }[] = [
    { value: 'contacts', labelKey: 'departmentVariables.tabs.contacts' },
    {
        value: 'departmentInfo',
        labelKey: 'departmentVariables.tabs.department',
    },
];

export default function SettingsOverview({
    settings,
    users,
    onSaved,
}: SettingsOverviewProps) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<VariableTab>('contacts');
    const [isAddingCategory, setIsAddingCategory] = useState(false);

    return (
        <Tabs
            value={activeTab}
            onValueChange={(value) => {
                setActiveTab(value as VariableTab);
                setIsAddingCategory(false);
            }}
        >
            <div className="mb-4 flex items-center justify-between gap-4">
                <TabsList>
                    {TABS.map((tab) => (
                        <TabsTrigger key={tab.value} value={tab.value}>
                            {t(tab.labelKey)}
                        </TabsTrigger>
                    ))}
                </TabsList>
                <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setIsAddingCategory(true)}
                >
                    <Plus className="h-4 w-4" />
                    {t('departmentVariables.category.addCategory')}
                </Button>
            </div>
            {TABS.map((tab) => {
                const tabCategories = settings.categories.filter(
                    (category) => category.tab === tab.value
                );
                return (
                    <TabsContent
                        key={tab.value}
                        value={tab.value}
                        className="space-y-3"
                    >
                        {isAddingCategory && (
                            <AddCategoryForm
                                tab={tab.value}
                                takenTitles={tabCategories.map((category) =>
                                    categoryTitle(category, t)
                                )}
                                onSaved={onSaved}
                                onClose={() => setIsAddingCategory(false)}
                            />
                        )}
                        {tabCategories.map((category) => (
                            <CategoryCard
                                key={category.key}
                                category={category}
                                variables={settings.variables.filter(
                                    (variable) =>
                                        variable.category === category.key
                                )}
                                allVariables={settings.variables}
                                tabCategories={tabCategories}
                                users={users}
                                onSaved={onSaved}
                            />
                        ))}
                    </TabsContent>
                );
            })}
        </Tabs>
    );
}
